import type {
  ChannelType,
  DeviceMediaItem,
  DevicePlaylist,
  DeviceSync,
  PlaybackIntent,
  ResolvedSlot,
} from "@workspace/api-client-react";
import { t } from "@workspace/strings/common";
import { shuffleSeedForPlaylist } from "./seeded-shuffle.ts";
import {
  materializeRotation,
  pickRotationItem,
  type RotationMode,
} from "./rotation.ts";
import { resolveSyncGroupId } from "./sync-group.ts";

/** Why a given item is on air — surfaced in the now-playing UI and play-logs. */
export type PlayReason = "music" | "ad" | "announcement";

/** A fully-resolved thing to put on air, with the context that justifies it. */
export interface QueueEntry {
  item: DeviceMediaItem;
  reason: PlayReason;
  channelType: ChannelType;
  playlistId: string | null;
  playlistName: string | null;
  scheduleName: string | null;
  label: string | null;
  /** True for priority insertions (ads/announcements) that interrupt music. */
  interrupt: boolean;
  /** Set when the entry originates from a timed event with explicit intent. */
  playbackIntent?: PlaybackIntent | null;
  /** Per-item still-image duration from the schedule entry. */
  imageDisplayDurationSec?: number | null;
  /**
   * "manual" marks an operator-started entry (play-this-playlist-now). It
   * keeps its playlist attribution for now-playing/play-logs, but soft
   * resync must judge it like a manual one-off — by its own content, never
   * against the channel's scheduled slot (it is by definition not the
   * scheduled content, and an unrelated edit must not cut it).
   */
  origin?: "manual";
}

export function channelSlot(
  sync: DeviceSync | null,
  type: ChannelType,
): ResolvedSlot | null {
  if (!sync) return null;
  const ch = sync.plan.channels.find((c) => c.channelType === type);
  return ch?.now ?? null;
}

/**
 * The screen bus's active slot at `at`. When the payload carries a
 * `screenTimeline` (every screen-bus window in the sync horizon, signage
 * hydrated — CB-024/CB-028) it is authoritative: the window containing `at`
 * wins, and no window means the screen is idle — even if the plan's now/next
 * snapshot still shows an old window. Payloads without the field (cached
 * before it shipped) fall back to the plan's now slot.
 */
export function screenSlotAt(
  sync: DeviceSync | null,
  at: number,
): ResolvedSlot | null {
  if (!sync) return null;
  const timeline = sync.screenTimeline;
  if (!Array.isArray(timeline)) return channelSlot(sync, "video");
  for (const slot of timeline) {
    if (!slot.startsAt || !slot.endsAt) continue;
    const startsAt = new Date(slot.startsAt).getTime();
    const endsAt = new Date(slot.endsAt).getTime();
    if (startsAt <= at && at < endsAt) return slot;
  }
  return null;
}

export function isSlotActive(slot: ResolvedSlot | null): boolean {
  if (!slot || slot.source === "none") return false;
  return Boolean(slot.playlistId || slot.mediaAssetId);
}

/**
 * Base seed for deterministic shuffle (sync group, server seed, or location +
 * device). Every branch derives the daily component from PAYLOAD data, never
 * from the device's own clock: `deterministicSeed` carries the server's day
 * (`${syncGroupId}:${day}`), and the fallbacks append the payload's
 * generatedAt day — so rotations still reshuffle daily, but the server
 * timeline and every device in a group derive byte-identical seeds
 * regardless of when (or with what clock skew) each side computes.
 */
export function syncShuffleBase(sync: DeviceSync | null): string {
  if (sync?.deterministicSeed) return sync.deterministicSeed;
  if (!sync?.device) return "omnimedia";
  const day =
    typeof sync.generatedAt === "string" && sync.generatedAt.length >= 10
      ? `:${sync.generatedAt.slice(0, 10)}`
      : "";
  const group = resolveSyncGroupId(
    sync.device.settings,
    sync.device.locationId ?? null,
  );
  if (group) return `${group}${day}`;
  const loc = sync.device.locationId ?? "default";
  return `${loc}:${sync.device.id}${day}`;
}

/** Locate a hydrated media item by asset id in the sync payload. */
export function mediaAssetById(
  sync: DeviceSync | null,
  mediaAssetId: string | null | undefined,
): DeviceMediaItem | null {
  if (!sync || !mediaAssetId) return null;
  for (const playlist of sync.playlists) {
    const found = playlist.items.find((item) => item.id === mediaAssetId);
    if (found) return found;
  }
  return null;
}

export function playlistById(
  sync: DeviceSync | null,
  playlistId: string | null | undefined,
): DevicePlaylist | null {
  if (!sync || !playlistId) return null;
  return sync.playlists.find((p) => p.id === playlistId) ?? null;
}

/**
 * Build the ordered rotation SEQUENCE for a playlist. Honors the playlist's
 * `rotationMode` (weight-aware via materializeRotation — "weighted" repeats
 * heavier items within the cycled sequence); playlists without one keep the
 * legacy `shuffle` mapping byte-identically (shuffle → one seeded
 * permutation, else position order). The server's sync-group segment
 * timeline materializes the SAME sequence from the SAME seed, so timeline
 * alignment indexes directly into this array.
 */
export function buildRotation(
  playlist: DevicePlaylist,
  shuffleBase?: string | null,
): DeviceMediaItem[] {
  const playable = playlist.items.filter((i) => Boolean(i.url));
  const seed = shuffleSeedForPlaylist(
    shuffleBase ?? playlist.id,
    playlist.id,
  );
  return materializeRotation(playable, playlistRotationMode(playlist), seed);
}

// ── CB-015: consume the server's resolved rotation at playback ─────────────
//
// The arbitration composer (arbitration.ts, via pickRotationItem) picks the
// item for a playlist slot honoring the schedule entry's `rotationMode` +
// per-item `weight`, and ships it as `ResolvedSlot.itemId` (device/plan,
// device/sync, schedule/resolve — same composer as preview). The helpers
// below make the players prefer that resolved itemId once per slot
// occupancy, then fall back to a local pick when the server hasn't resolved
// one (offline, no sync yet, or a stale/filtered cache). Multi-room devices
// skip the anchor entirely: the sync-group segment timeline (built from the
// same materializeRotation sequence as `buildRotation` above) owns the index
// there, and it is mode/weight-aware itself.
//
// A resync is periodic (minutes), not per-track, so a slot's `itemId` stays
// unchanged across many advances within one sync — both helpers track the
// last-consumed resolved id so it is applied exactly once per occupancy
// instead of replaying the same item forever until the next sync.

/**
 * Anchor for a materialized rotation array (e.g. `buildRotation`'s output) to
 * the server-resolved item for a slot, without reordering the array itself.
 * Used where the array's exact order is load-bearing for something else —
 * the music channel's multi-room sync-group alignment (see
 * `@/player/lib/sync-group` `alignMusicToTimeline` / `segment-timeline.ts`)
 * independently derives the SAME order from the SAME seed, so the array
 * can't be reshuffled/rebuilt here without breaking that cross-device match.
 */
export interface RotationAnchor {
  consumedItemId: string | null;
}

export function createRotationAnchor(): RotationAnchor {
  return { consumedItemId: null };
}

/**
 * Given a materialized rotation `array` and the slot it was built for, return
 * the index to consume next: the resolved item's position when `slot.itemId`
 * is fresh (not yet consumed via this anchor) and present in `array`;
 * otherwise `currentIndex` unchanged (the caller's own local walk/fallback).
 */
export function anchorRotationIndex(
  array: readonly DeviceMediaItem[],
  slot: ResolvedSlot | null | undefined,
  currentIndex: number,
  anchor: RotationAnchor,
): number {
  const resolvedId = slot?.itemId ?? null;
  if (!resolvedId || resolvedId === anchor.consumedItemId) return currentIndex;
  anchor.consumedItemId = resolvedId;
  const idx = array.findIndex((item) => item.id === resolvedId);
  return idx >= 0 ? idx : currentIndex;
}

/**
 * Local fallback rotation state for a channel that has no other authoritative
 * ordering to protect (no multi-room sync-group dependency) — used with
 * `nextRotationItem` below. Drives the shared `pickRotationItem` engine (the
 * SAME algorithm the server's arbitration composer uses to produce
 * `ResolvedSlot.itemId`) instead of a one-shot shuffle, so an extended
 * offline stretch still reshuffles fairly each full lap through the pool
 * rather than repeating one fixed order forever.
 */
export interface LocalRotationState {
  playlistId: string | null;
  counter: number;
  /** Last server-resolved itemId already consumed for this slot occupancy. */
  consumedItemId: string | null;
}

export function createLocalRotationState(): LocalRotationState {
  return { playlistId: null, counter: 0, consumedItemId: null };
}

/**
 * Rotation mode for a device playlist's local fallback rotation (CB-015).
 * Prefers the wire's `rotationMode` (shipped by the /device hydrators);
 * cached older payloads without it map the legacy `shuffle` boolean.
 */
export function playlistRotationMode(playlist: DevicePlaylist): RotationMode {
  return playlist.rotationMode ?? (playlist.shuffle ? "even" : "sequential");
}

/**
 * Pick the next item to play from a playlist slot: prefer the server's
 * resolved rotation decision (`slot.itemId`) exactly once per slot occupancy,
 * falling back to `pickRotationItem` — keyed the same way per playlist, mode
 * from the playlist's wire `rotationMode` (legacy shuffle mapping for cached
 * older payloads), with per-item `weight` feeding "weighted" mode — when the
 * server hasn't resolved an item, it was already consumed, or it isn't in the
 * locally-hydrated pool.
 */
export function nextRotationItem(
  playlist: DevicePlaylist,
  slot: ResolvedSlot | null | undefined,
  shuffleBase: string | null | undefined,
  state: LocalRotationState,
): DeviceMediaItem | null {
  const playable = playlist.items.filter((i) => Boolean(i.url));
  if (playable.length === 0) return null;

  if (state.playlistId !== playlist.id) {
    state.playlistId = playlist.id;
    state.counter = 0;
    state.consumedItemId = null;
  }

  const resolvedId = slot?.itemId ?? null;
  if (resolvedId && resolvedId !== state.consumedItemId) {
    state.consumedItemId = resolvedId;
    const match = playable.find((i) => i.id === resolvedId);
    if (match) {
      state.counter += 1;
      return match;
    }
  }

  const mode: RotationMode = playlistRotationMode(playlist);
  const key = shuffleSeedForPlaylist(shuffleBase ?? playlist.id, playlist.id);
  const picked = pickRotationItem(playable, mode, key, state.counter);
  state.counter += 1;
  return (picked as DeviceMediaItem | null) ?? null;
}

export function makeEntry(
  item: DeviceMediaItem,
  reason: PlayReason,
  slot: ResolvedSlot | null,
  playlist: DevicePlaylist | null,
  interrupt: boolean,
  extras?: {
    label?: string | null;
    playbackIntent?: PlaybackIntent | null;
    imageDisplayDurationSec?: number | null;
  },
): QueueEntry {
  return {
    item,
    reason,
    channelType: playlist?.channelType ?? slot?.channelType ?? "music",
    playlistId: playlist?.id ?? slot?.playlistId ?? null,
    playlistName: playlist?.name ?? slot?.playlistName ?? null,
    scheduleName: slot?.scheduleName ?? null,
    label: extras?.label ?? slot?.label ?? null,
    interrupt,
    playbackIntent: extras?.playbackIntent ?? null,
    imageDisplayDurationSec: extras?.imageDisplayDurationSec ?? null,
  };
}

export function playReasonForChannel(
  channelType: ChannelType,
): PlayReason {
  switch (channelType) {
    case "ads":
      return "ad";
    case "announcements":
      return "announcement";
    default:
      return "music";
  }
}

/** Human-readable reason for the now-playing surface. */
export function reasonLabel(entry: QueueEntry | null): string {
  if (!entry) return t("player.idle");
  switch (entry.reason) {
    case "announcement":
      return entry.label
        ? t("player.announcementLabeled", { label: entry.label })
        : t("player.announcement");
    case "ad":
      return entry.label
        ? t("player.adBreakLabeled", { label: entry.label })
        : t("player.adBreak");
    default:
      return entry.scheduleName
        ? t("player.musicLabeled", { name: entry.scheduleName })
        : entry.playlistName
          ? t("player.musicLabeled", { name: entry.playlistName })
          : t("player.music");
  }
}

/**
 * Why the player isn't playing anything right now. Used to show a specific
 * diagnostic message instead of the generic "Nothing scheduled" fallback.
 */
export type IdleReason =
  | "no-schedule"
  | "channel-disabled"
  | "no-playable-tracks"
  | "autoplay-blocked";

export interface IdleInfo {
  reason: IdleReason;
  playlistName: string | null;
}

/**
 * Derive *why* there is nothing playing from the current sync snapshot.
 */
export function deriveIdleReason(
  sync: DeviceSync | null,
  autoplayBlocked = false,
): IdleInfo {
  if (autoplayBlocked) return { reason: "autoplay-blocked", playlistName: null };

  if (!sync) return { reason: "no-schedule", playlistName: null };

  const hasChannel = sync.plan.channels.some((c) => c.channelType === "music");
  if (!hasChannel) return { reason: "channel-disabled", playlistName: null };

  const slot = channelSlot(sync, "music");
  if (!isSlotActive(slot)) return { reason: "no-schedule", playlistName: null };

  if (slot?.mediaAssetId) {
    const item = mediaAssetById(sync, slot.mediaAssetId);
    if (!item?.url) {
      return { reason: "no-playable-tracks", playlistName: slot.playlistName ?? null };
    }
    return { reason: "no-schedule", playlistName: null };
  }

  const playlist = playlistById(sync, slot?.playlistId);
  if (playlist && buildRotation(playlist, syncShuffleBase(sync)).length === 0) {
    return {
      reason: "no-playable-tracks",
      playlistName: playlist.name ?? slot?.playlistName ?? null,
    };
  }

  return { reason: "no-schedule", playlistName: null };
}

/** Serializable queue row for realtime now-playing and command results. */
export interface QueueSnapshotItem {
  itemId: string;
  title: string;
  artist: string | null;
  channelType: ChannelType;
  reason: PlayReason;
  playlistName: string | null;
}

/**
 * Per-channel now-playing state the player publishes alongside the device-level
 * summary, so control surfaces can render full transport for each channel.
 */
export interface ChannelNowPlaying {
  channelType: ChannelType;
  bus: "audio" | "screen";
  state: "playing" | "paused" | "stopped";
  title: string | null;
  artist: string | null;
  itemId: string | null;
  playlistId: string | null;
  playlistName: string | null;
  positionSec: number;
  durationSec: number | null;
  volume: number;
  muted: boolean;
}

export function toQueueSnapshot(entry: QueueEntry): QueueSnapshotItem {
  return {
    itemId: entry.item.id,
    title: entry.item.title,
    artist: entry.item.artist ?? null,
    channelType: entry.channelType,
    reason: entry.reason,
    playlistName: entry.playlistName,
  };
}

export function formatTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const s = Math.floor(totalSeconds % 60);
  const m = Math.floor(totalSeconds / 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
